import { cdn } from '@/lib/cdn';
import { MediaToggleButton } from '../MediaToggleButton';
import clsx from 'clsx';
import styles from './ExpandableColumnStack.module.scss';

interface ExpandableColumnStackItemProps {
  item: ExpandableItem;
  index: number;
  isActive: boolean;
  isPlaying: boolean;
  canPlayVideo: boolean;
  transitionComplete: boolean;
  videoUrl: string | null;
  mobilePositionClass: string | undefined;
  skipTransition: boolean;
  testIdPrefix: string;
  itemAriaLabel: string | undefined;
  controlAriaLabel: string;
  controlIcon: JSX.Element;
  videoRef: (el: HTMLVideoElement | null) => void;
  onItemClick: (index: number) => void;
  onPlayPause: () => void;
  onVideoCanPlay: (index: number) => void;
}

export interface ExpandableItem {
  /** Unique identifier */
  id: string;
  /** Display text (centered on active item) */
  title?: string | null;
  /** Optional subtitle */
  subtitle?: string | null;
  /** Static image URL (shown when inactive) */
  imageUrl?: string | null;
  /** Mobile image URL */
  imageMobileUrl?: string | null;
  /** Video URL (played when active) */
  videoUrl?: string | null;
  /** Mobile video URL */
  videoMobileUrl?: string | null;
  /** Alt text for accessibility */
  alt?: string | null;
}

function ItemMedia({
  item,
  index,
  videoUrl,
  videoRef,
  isPlaying,
  canPlayVideo,
  onVideoCanPlay,
}: Readonly<{
  item: ExpandableItem;
  index: number;
  videoUrl: string | null;
  videoRef: (el: HTMLVideoElement | null) => void;
  isPlaying: boolean;
  canPlayVideo: boolean;
  onVideoCanPlay: (index: number) => void;
}>) {
  return (
    <>
      {item.imageUrl && (
        <picture>
          {item.imageMobileUrl && (
            <source
              media="(max-width: 767px)"
              srcSet={cdn.transformImageUrl(item.imageMobileUrl, {
                width: 828,
                quality: 80,
                format: 'auto',
              })}
            />
          )}
          <img
            src={cdn.transformImageUrl(item.imageUrl, {
              width: 1920,
              quality: 85,
              format: 'auto',
            })}
            alt={item.alt || ''}
            className={clsx(
              styles.media,
              styles.image,
              isPlaying && canPlayVideo ? styles.fadeOut : styles.fadeIn
            )}
            loading={index === 0 ? 'eager' : 'lazy'}
          />
        </picture>
      )}

      {videoUrl && (
        <video
          key={videoUrl}
          ref={videoRef}
          className={clsx(styles.media, styles.video)}
          muted
          loop
          playsInline
          onCanPlay={() => onVideoCanPlay(index)}
        >
          <source src={cdn.transformVideoUrl(videoUrl)} type="video/mp4" />
        </video>
      )}
    </>
  );
}

export function ExpandableColumnStackItem({
  item,
  index,
  isActive,
  isPlaying,
  canPlayVideo,
  transitionComplete,
  videoUrl,
  mobilePositionClass,
  skipTransition,
  testIdPrefix,
  itemAriaLabel,
  controlAriaLabel,
  controlIcon,
  videoRef,
  onItemClick,
  onPlayPause,
  onVideoCanPlay,
}: Readonly<ExpandableColumnStackItemProps>) {
  const itemClassName = clsx(
    styles.item,
    isActive ? styles.active : styles.inactive,
    mobilePositionClass,
    skipTransition && styles.noTransition,
    !canPlayVideo && styles.noVideo
  );
  const testId = `${testIdPrefix}-item-${index}`;

  const showOverlayButton = !isActive;
  const showContentOverlay = isActive;
  const showControl = isActive ? canPlayVideo : true;

  return (
    <div className={itemClassName} data-testid={testId} data-active={isActive}>
      {showOverlayButton && (
        <button
          type="button"
          className={styles.itemOverlayButton}
          onClick={() => onItemClick(index)}
          aria-label={itemAriaLabel}
        />
      )}

      <ItemMedia
        item={item}
        index={index}
        videoUrl={videoUrl}
        videoRef={videoRef}
        isPlaying={isPlaying}
        canPlayVideo={canPlayVideo}
        onVideoCanPlay={onVideoCanPlay}
      />

      {showContentOverlay && (
        <div className={clsx(styles.overlay, transitionComplete && styles.overlayVisible)}>
          {item.title && <h3 className={styles.title}>{item.title}</h3>}
          {item.subtitle && <p className={styles.subtitle}>{item.subtitle}</p>}
        </div>
      )}

      {showControl && (
        <div className={styles.controlButton}>
          <MediaToggleButton
            onActivate={isActive ? onPlayPause : () => onItemClick(index)}
            ariaLabel={controlAriaLabel}
            testId={`${testIdPrefix}-control-${index}`}
          >
            {controlIcon}
          </MediaToggleButton>
        </div>
      )}
    </div>
  );
}
